'use client';

import { c as _c } from "react/compiler-runtime";
import { GoogleTagManager } from '@next/third-parties/google';
import { CookieBanner, useCookieBannerAutoReject } from 'design-system/client';
import { useLocale, useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import Hotjar from './Hotjar';
import { ClientCookieBannerProps } from './types';
import useAnalytics from './useAnalytics';
import useTrackingCookie from './useTrackingCookie';
import I18nHtml from '@/components/I18nHtml';
import { report } from '@/errorReporting';
import { RailsRoutes } from '@/helpers/utils';
function ClientCookieBanner(props) {
  const $ = _c(4);
  const [shouldShowCookieBanner, setShouldShowCookieBanner] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      if (window.top !== window.self) {
        report(new Error("Accessing cookie related code from iframe"));
        return;
      }
      const timeout = setTimeout(() => setShouldShowCookieBanner(true), 0);
      return () => clearTimeout(timeout);
    };
    t1 = [setShouldShowCookieBanner];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  if (!shouldShowCookieBanner) {
    return null;
  }
  let t2;
  if ($[2] !== props) {
    t2 = <ClientCookieBannerInner {...props} />;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function ClientCookieBannerInner(t0) {
  const $ = _c(49);
  const {
    domain,
    environment,
    serverSideCookieValue,
    gtmAuth,
    gtmId,
    gtmPreview,
    hotjarId
  } = t0;
  const [trackingCookieValue, setTrackingCookie] = useTrackingCookie(serverSideCookieValue, domain);
  const t = useTranslations("nextjs");
  const locale = useLocale();
  let t1;
  if ($[0] !== setTrackingCookie) {
    t1 = () => {
      setTrackingCookie("accepted");
    };
    $[0] = setTrackingCookie;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const acceptTracking = t1;
  let t2;
  if ($[2] !== setTrackingCookie) {
    t2 = () => {
      setTrackingCookie("rejected");
    };
    $[2] = setTrackingCookie;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const rejectTracking = t2;
  const cookieBannerRef = useCookieBannerAutoReject(rejectTracking, false, trackingCookieValue);
  useAnalytics(trackingCookieValue);
  if (trackingCookieValue === undefined) {
    return null;
  }
  if (environment === "test") {
    return null;
  }
  if (trackingCookieValue === "rejected") {
    return null;
  }
  if (trackingCookieValue === "accepted") {
    const gtmEnabled = gtmAuth && gtmId && gtmPreview;
    let t3;
    if ($[4] !== gtmAuth || $[5] !== gtmEnabled || $[6] !== gtmId || $[7] !== gtmPreview) {
      t3 = !!gtmEnabled && <GoogleTagManager gtmId={gtmId} auth={gtmAuth} preview={gtmPreview} />;
      $[4] = gtmAuth;
      $[5] = gtmEnabled;
      $[6] = gtmId;
      $[7] = gtmPreview;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] !== hotjarId) {
      t4 = <Hotjar hotjarId={hotjarId} />;
      $[9] = hotjarId;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== t3 || $[12] !== t4) {
      t5 = <>{t3}{t4}</>;
      $[11] = t3;
      $[12] = t4;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  let t3;
  if ($[14] !== t) {
    t3 = t("cookie_banner.headline");
    $[14] = t;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  let t4;
  if ($[16] !== t) {
    t4 = t("cookie_banner.introduction_text");
    $[16] = t;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  let t5;
  if ($[18] !== t) {
    t5 = t("cookie_banner.call_for_action");
    $[18] = t;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t) {
    t6 = t("cookie_banner.information_headline");
    $[20] = t;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== locale) {
    t7 = RailsRoutes.privacyPolicy({
      locale
    });
    $[22] = locale;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] !== t7) {
    t8 = <I18nHtml i18nKey="cookie_banner.information_details_html" options={{
      href: t7,
      target: "_blank"
    }} />;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t) {
    t9 = t("cookie_banner.reject_button");
    $[26] = t;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t) {
    t10 = t("cookie_banner.accept_button");
    $[28] = t;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== t) {
    t11 = t("cookie_banner.information_button");
    $[30] = t;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t) {
    t12 = t("cookie_banner.close_button_label");
    $[32] = t;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t12 || $[37] !== t3 || $[38] !== t4 || $[39] !== t5 || $[40] !== t6 || $[41] !== t8 || $[42] !== t9) {
    t13 = {
      headline: t3,
      introductionText: t4,
      callForAction: t5,
      informationHeadline: t6,
      informationDetailsContent: t8,
      rejectButtonLabel: t9,
      acceptButtonLabel: t10,
      informationButtonLabel: t11,
      closeButtonLabel: t12
    };
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
    $[37] = t3;
    $[38] = t4;
    $[39] = t5;
    $[40] = t6;
    $[41] = t8;
    $[42] = t9;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== acceptTracking || $[45] !== cookieBannerRef || $[46] !== rejectTracking || $[47] !== t13) {
    t14 = <CookieBanner ref={cookieBannerRef} onAcceptTracking={acceptTracking} onRejectTracking={rejectTracking} text={t13} />;
    $[44] = acceptTracking;
    $[45] = cookieBannerRef;
    $[46] = rejectTracking;
    $[47] = t13;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  return t14;
}
export default ClientCookieBanner;